import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown, Clock, AlertTriangle, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

// Time conversion utilities with enhanced safety
/**
 * Safely converts seconds to a formatted time string
 * @param seconds - Seconds since midnight (0-86399)
 * @param format - Time format ('12h' or '24h')
 * @returns Formatted time string
 * @throws Will handle invalid input gracefully
 */
const secondsToTimeString = (
  seconds: number,
  format: '12h' | '24h' = '12h'
): string => {
  try {
    // Input validation with type safety
    if (typeof seconds !== 'number' || !isFinite(seconds)) {
      return '--:--'
    }

    // Ensure seconds are within valid range
    const validSeconds = Math.max(0, Math.min(86399, Math.floor(seconds)))

    const hours = Math.floor(validSeconds / 3600)
    const minutes = Math.floor((validSeconds % 3600) / 60)

    if (format === '12h') {
      const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
      const ampm = hours < 12 ? 'AM' : 'PM'
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`
    } else {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    }
  } catch (error) {
    // Graceful fallback for any conversion errors
    return '--:--'
  }
}

/**
 * Safely converts time string to seconds since midnight
 * @param timeStr - Time string in format 'HH:MM AM/PM' or 'HH:MM'
 * @returns Seconds since midnight (0-86399)
 * @throws Will handle invalid input gracefully
 */
const timeStringToSeconds = (timeStr: string): number => {
  try {
    // Input validation
    if (typeof timeStr !== 'string' || !timeStr.trim()) {
      return 0
    }

    const cleanTimeStr = timeStr.trim()
    const isAmPm = cleanTimeStr.includes('AM') || cleanTimeStr.includes('PM')

    if (isAmPm) {
      const parts = cleanTimeStr.split(' ')
      if (parts.length !== 2) return 0

      const [timePart, period] = parts
      const timeParts = timePart.split(':')
      if (timeParts.length !== 2) return 0

      const hours = parseInt(timeParts[0], 10)
      const minutes = parseInt(timeParts[1], 10)

      if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 1 ||
        hours > 12 ||
        minutes < 0 ||
        minutes >= 60
      ) {
        return 0
      }

      let hour24 = hours
      if (period === 'AM' && hours === 12) hour24 = 0
      else if (period === 'PM' && hours !== 12) hour24 = hours + 12

      return hour24 * 3600 + minutes * 60
    } else {
      const timeParts = cleanTimeStr.split(':')
      if (timeParts.length !== 2) return 0

      const hours = parseInt(timeParts[0], 10)
      const minutes = parseInt(timeParts[1], 10)

      if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours >= 24 ||
        minutes < 0 ||
        minutes >= 60
      ) {
        return 0
      }

      return hours * 3600 + minutes * 60
    }
  } catch (error) {
    // Graceful fallback for parsing errors
    return 0
  }
}

/**
 * Generate time options for dropdown with enhanced error handling
 * @param format - Time format ('12h' or '24h')
 * @param interval - Minutes between options (1-60)
 * @returns Array of time options with value and label
 * @throws Will provide fallback options on error
 */
const generateTimeOptions = (
  format: '12h' | '24h',
  interval: number = 1
): Array<{ value: number; label: string }> => {
  try {
    // Input validation
    const validFormat = format === '12h' || format === '24h' ? format : '12h'
    const validInterval = Math.max(1, Math.min(60, Math.floor(interval || 1)))

    const options: Array<{ value: number; label: string }> = []

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += validInterval) {
        const seconds = Math.floor(hour * 3600 + minute * 60)
        const label = secondsToTimeString(seconds, validFormat)

        // Only add valid options
        if (label && label !== '--:--') {
          options.push({ value: seconds, label })
        }
      }
    }

    // Fallback if no options generated
    if (options.length === 0) {
      return [
        { value: 0, label: '12:00 AM' },
        { value: 43200, label: '12:00 PM' },
      ]
    }

    return options
  } catch (error) {
    // Fallback options for any generation errors
    return [
      { value: 0, label: '12:00 AM' },
      { value: 21600, label: '6:00 AM' },
      { value: 43200, label: '12:00 PM' },
      { value: 64800, label: '6:00 PM' },
    ]
  }
}

/**
 * Props interface for TimeDropdown component with comprehensive type safety
 */
interface TimeDropdownProps {
  /**
   * Current time value in seconds since midnight (0-86399)
   * @default undefined
   */
  value?: number | null

  /**
   * Callback fired when time value changes
   * @param value - New time value in seconds
   */
  onChange?: (value: number) => void

  /**
   * Time display format
   * @default '12h'
   */
  format?: '12h' | '24h'

  /**
   * Minutes between time options (1-60)
   * @default 1
   */
  interval?: number

  /**
   * Whether the dropdown is disabled
   * @default false
   */
  disabled?: boolean

  /**
   * Placeholder text when no value selected
   * @default 'Select time'
   */
  placeholder?: string

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * HTML id attribute
   */
  id?: string

  /**
   * Accessibility label
   */
  'aria-label'?: string
}

const TimeDropdown = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  TimeDropdownProps
>(
  (
    {
      value,
      onChange,
      format = '12h',
      interval = 1,
      disabled = false,
      placeholder = 'Select time',
      className,
      id,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)

    // Memoize time options generation for performance with error handling
    const timeOptions = React.useMemo(() => {
      try {
        return generateTimeOptions(format, interval)
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'TimeDropdown: Failed to generate time options, using fallback:',
            error
          )
        }
        // Fallback to basic options
        return [
          { value: 0, label: '12:00 AM' },
          { value: 21600, label: '6:00 AM' },
          { value: 43200, label: '12:00 PM' },
          { value: 64800, label: '6:00 PM' },
        ]
      }
    }, [format, interval])

    // Memoize selected option lookup for performance with null safety
    const selectedOption = React.useMemo(() => {
      if (
        value === null ||
        value === undefined ||
        !Array.isArray(timeOptions)
      ) {
        return undefined
      }

      try {
        return timeOptions.find(option => option && option.value === value)
      } catch (error) {
        return undefined
      }
    }, [value, timeOptions])

    // Handle value change with comprehensive error handling and validation
    const handleValueChange = React.useCallback(
      (stringValue: string) => {
        try {
          // Input validation
          if (typeof stringValue !== 'string' || !stringValue.trim()) {
            return
          }

          const numValue = parseInt(stringValue, 10)

          // Comprehensive validation
          if (
            !isNaN(numValue) &&
            isFinite(numValue) &&
            numValue >= 0 &&
            numValue < 86400 && // Valid seconds in a day
            typeof onChange === 'function'
          ) {
            onChange(numValue)
            // Explicitly close dropdown after successful selection
            setOpen(false)
          }
        } catch (error) {
          // Graceful error handling - component continues to function
          // Error is logged in development but not in production
          if (process.env.NODE_ENV === 'development') {
            console.warn('TimeDropdown: Invalid value change:', {
              stringValue,
              error,
            })
          }
        }
      },
      [onChange, setOpen]
    )

    return (
      <SelectPrimitive.Root
        value={value?.toString()}
        onValueChange={handleValueChange}
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          id={id}
          aria-label={ariaLabel || 'Select time'}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-4 py-2 text-sm ring-offset-background',
            'data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
            // Enhanced mobile touch targets - VRP Design System compliance
            'min-h-[44px] sm:min-h-[40px] touch-manipulation',
            // VRP Design System: Professional appearance
            'transition-colors duration-200',
            className
          )}
          data-slot="time-dropdown-trigger"
        >
          <div className="flex items-center gap-2">
            <Clock
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <SelectPrimitive.Value placeholder={placeholder}>
              {selectedOption?.label || placeholder}
            </SelectPrimitive.Value>
          </div>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={cn(
              'relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden',
              'rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-none',
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              'origin-[--radix-select-content-transform-origin]',
              // Enhanced momentum scrolling on mobile
              'overscroll-behavior-y-contain'
            )}
            position="popper"
            data-slot="time-dropdown-content"
            sideOffset={4}
          >
            <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-2 min-h-[36px]">
              <ChevronDown className="h-4 w-4 rotate-180" />
            </SelectPrimitive.ScrollUpButton>

            <SelectPrimitive.Viewport className="p-1 max-h-[200px] overflow-y-auto">
              {timeOptions.map(option => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value.toString()}
                  className={cn(
                    'relative flex w-full cursor-default select-none items-center rounded-sm py-2.5 pl-8 pr-2 text-sm outline-none',
                    'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                    // Enhanced mobile touch targets following VRP 8pt grid system
                    'min-h-[44px] sm:min-h-[36px] py-2 touch-manipulation',
                    // VRP Design System compliance - Typography
                    'text-sm font-normal hover:bg-accent/50 transition-colors duration-150'
                  )}
                  data-slot="time-dropdown-item"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <div className="h-2 w-2 rounded-full bg-current" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>
                    {option.label}
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>

            <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-2 min-h-[36px]">
              <ChevronDown className="h-4 w-4" />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    )
  }
)

TimeDropdown.displayName = 'TimeDropdown'

// =============================================================================
// ERROR BOUNDARY IMPLEMENTATION
// =============================================================================

/**
 * Error boundary state for TimeDropdown components
 */
interface TimeDropdownErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorCount: number
}

/**
 * Error boundary props for TimeDropdown
 */
interface TimeDropdownErrorBoundaryProps {
  children: React.ReactNode
  context?: string
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

/**
 * Production-grade error boundary for TimeDropdown component
 */
class TimeDropdownErrorBoundary extends React.Component<
  TimeDropdownErrorBoundaryProps,
  TimeDropdownErrorBoundaryState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null

  constructor(props: TimeDropdownErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      errorCount: 0,
    }
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<TimeDropdownErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError, context } = this.props

    this.setState(prevState => ({
      errorCount: prevState.errorCount + 1,
    }))

    // Log error in development only
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `TimeDropdown Error${context ? ` (${context})` : ''}:`,
        error
      )
    }

    // Call external error handler if provided
    if (typeof onError === 'function') {
      try {
        onError(error, errorInfo)
      } catch (reportingError) {
        // Don't let error reporting break the boundary
        if (process.env.NODE_ENV === 'development') {
          console.warn('TimeDropdown error reporting failed:', reportingError)
        }
      }
    }
  }

  private handleRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }

    this.retryTimeoutId = setTimeout(() => {
      this.setState({
        hasError: false,
        error: undefined,
      })
    }, 100)
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
  }

  render() {
    const { hasError, error, errorCount } = this.state
    const { children, context } = this.props

    if (hasError && error) {
      const isRepeatedError = errorCount > 1

      return (
        <div
          className={cn(
            'flex flex-col items-center justify-center p-4 border border-red-200 rounded-md bg-red-50',
            'min-h-[80px] space-y-3'
          )}
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle
              className="h-4 w-4 flex-shrink-0"
              aria-hidden="true"
            />
            <span className="text-sm font-semibold">
              Time input error{context ? ` (${context})` : ''}
            </span>
          </div>

          <p className="text-sm text-red-600 text-center">
            {isRepeatedError
              ? 'Multiple errors detected. Please refresh the page.'
              : 'Please try again or refresh the page.'}
          </p>

          {!isRepeatedError && (
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleRetry}
              className="h-7 px-4 text-sm border-red-300 text-red-700 hover:bg-red-100"
              aria-label="Retry time input"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Try Again
            </Button>
          )}
        </div>
      )
    }

    return children
  }
}

/**
 * Safe TimeDropdown component wrapped with error boundary
 */
const SafeTimeDropdown = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  TimeDropdownProps & {
    errorBoundaryContext?: string
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  }
>(({ errorBoundaryContext, onError, ...props }, ref) => {
  return (
    <TimeDropdownErrorBoundary context={errorBoundaryContext} onError={onError}>
      <TimeDropdown {...props} ref={ref} />
    </TimeDropdownErrorBoundary>
  )
})

SafeTimeDropdown.displayName = 'SafeTimeDropdown'

export {
  TimeDropdown,
  SafeTimeDropdown,
  secondsToTimeString,
  timeStringToSeconds,
}
export type { TimeDropdownProps }
